// Profile links for the social handles users enter in the Bidsy app.
//
// The URL formats are the easy part. What breaks links is the handle itself:
// users type display names ("Farhad Ahmad"), paste whole profile URLs, or
// enter the platform's name. getSocialLink cleans what it can and reports what
// it can't, so a reviewer knows a link will fail before they click it.
//
// Character rules follow each platform's current username rules, kept a little
// lenient so real accounts are never flagged: X allows old handles shorter than
// its current 4-character minimum, and YouTube handles may use non-Latin letters.

const PLATFORMS = {
  instagram: {
    label: 'Instagram',
    color: '#E1306C',
    hosts: ['instagram.com'],
    allowed: /^[A-Za-z0-9._]+$/,
    allowedText: 'letters, numbers, periods and underscores',
    min: 1,
    max: 30,
    url: (h) => `https://www.instagram.com/${h}/`,
  },
  tiktok: {
    label: 'TikTok',
    color: '#000000',
    hosts: ['tiktok.com'],
    allowed: /^[A-Za-z0-9._]+$/,
    allowedText: 'letters, numbers, periods and underscores',
    min: 2,
    max: 24,
    url: (h) => `https://www.tiktok.com/@${h}`,
  },
  youtube: {
    label: 'YouTube',
    color: '#FF0000',
    hosts: ['youtube.com'],
    allowed: /^[\p{L}\p{N}._-]+$/u,
    allowedText: 'letters, numbers, periods, hyphens and underscores',
    min: 3,
    max: 30,
    url: (h) => `https://www.youtube.com/@${h}`,
  },
  x_com: {
    label: 'X',
    color: '#1DA1F2',
    hosts: ['x.com', 'twitter.com'],
    allowed: /^[A-Za-z0-9_]+$/,
    allowedText: 'letters, numbers and underscores',
    min: 1,
    max: 15,
    url: (h) => `https://x.com/${h}`,
  },
};
PLATFORMS.x = PLATFORMS.x_com;

// A YouTube channel ID ("UC" + 22 characters) is a real address, just not a handle.
const YOUTUBE_CHANNEL_ID = /^UC[A-Za-z0-9_-]{22}$/;

// Names people enter instead of their handle, e.g. "Tiktok" in the TikTok box.
const PLATFORM_NAMES = new Set(['instagram', 'tiktok', 'youtube', 'x', 'x.com', 'twitter', 'twitter.com']);

// Reduce a pasted profile URL ("https://www.instagram.com/name/?igsh=...")
// to its first path segment. Anything that isn't a URL for this platform is
// returned unchanged.
function stripProfileUrl(value, hosts) {
  const match = value.match(/^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*([a-z0-9-]+\.[a-z]+)(\/[^?#]*)?/i);
  if (!match || !hosts.includes(match[1].toLowerCase())) return value;
  const segments = (match[2] || '').split('/').filter(Boolean);
  // youtube.com/channel/<id>, and the legacy /c/<name> and /user/<name>
  if (['channel', 'c', 'user'].includes(segments[0]) && segments[1]) return segments[1];
  return segments[0] || value;
}

function describeProblem(handle, platform) {
  if (/\s/.test(handle)) {
    return 'Has a space, so it\'s likely a display name, not a handle. The link won\'t find their profile.';
  }
  if (PLATFORM_NAMES.has(handle.toLowerCase())) {
    return 'This is the platform\'s own name, not the user\'s handle.';
  }
  if (!platform.allowed.test(handle)) {
    return `${platform.label} handles can only use ${platform.allowedText}, so the link won't find their profile.`;
  }
  if (handle.length < platform.min || handle.length > platform.max) {
    return `${platform.label} handles are ${platform.min}–${platform.max} characters and this is ${handle.length}, so the link won't find their profile.`;
  }
  return null;
}

export function platformLabel(type) {
  return PLATFORMS[type]?.label || type;
}

export function platformColor(type) {
  return PLATFORMS[type]?.color || '#666';
}

/**
 * @returns {{ url: string|null, problem: string|null }}
 *   url     – best profile link for the handle, or null for an unknown platform
 *   problem – why the handle can't be a real account on that platform, or null
 */
export function getSocialLink(type, rawHandle) {
  const platform = PLATFORMS[type];
  if (!platform) return { url: null, problem: null };

  const handle = stripProfileUrl((rawHandle || '').trim(), platform.hosts)
    .replace(/^@+/, '')
    .replace(/\/+$/, '');
  if (!handle) return { url: null, problem: 'No handle entered' };

  if (type === 'youtube' && YOUTUBE_CHANNEL_ID.test(handle)) {
    return { url: `https://www.youtube.com/channel/${handle}`, problem: null };
  }

  return {
    url: platform.url(encodeURIComponent(handle)),
    problem: describeProblem(handle, platform),
  };
}
